<template>
  <div class="enter-pii">
    <!-- Logo -->
    <div class="logo-wrapper">
      <img v-if="isDark" src="/images/logo-font-white.svg" alt="Yuzu" class="logo" />
      <img v-else src="/images/logo-font-black.svg" alt="Yuzu" class="logo" />
    </div>

    <!-- Heading -->
    <h2 class="enter-pii-title">{{ $t('app.auth.enter-pii.heading') }}</h2>

    <!-- Email Input -->
    <!-- Name Fields Row -->
    <div class="form-row">
      <div class="form-group">
        <label for="firstName" class="form-label">{{ $t('app.auth.enter-pii.firstName') }}</label>
        <input
          id="firstName"
          v-model="formData.firstName"
          type="text"
          class="form-input"
          placeholder="Dupont"
        />
      </div>

      <div class="form-group">
        <label for="lastName" class="form-label">{{ $t('app.auth.enter-pii.lastName') }}</label>
        <input
          id="lastName"
          v-model="formData.lastName"
          type="text"
          class="form-input"
          placeholder="Robert"
        />
      </div>
    </div>

    <div class="btn-group">
      <!-- Back Button -->
      <button class="btn-back"
        @click="back"
      >
        <i class="bi bi-arrow-return-left"></i>
      </button>

      <!-- Continue Button -->
      <button
        class="btn-continue"
        :disabled="!isFormValid || isLoading"
        @click="handleContinue"
      >
        <span v-if="!isLoading">{{ $t('app.auth.enter-pii.continue') }}</span>
        <span v-else>
          <i class="bi bi-arrow-repeat"></i>
        </span>
      </button>
    </div>


    <!-- Terms Text -->
    <p class="terms-text">
      {{ $t('app.auth.terms.text') }}
      <a href="https://www.yuzu.hr/conditions-generales-d-utilisation">{{ $t('app.auth.terms.cgu') }}</a>{{ $t('app.auth.terms.text2') }}
      <a href="https://www.yuzu.hr/politique-de-confidentialite">{{ $t('app.auth.terms.privacy') }}</a>
    </p>
  </div>
</template>

<script setup lang="ts">
import { authApi } from "@/api/authentification.api";
import { userApi } from "@/api/user.api";
import { SubmitPiiError } from "@/types/auth";
import { useDark } from "@vueuse/core";
import { ref, reactive, computed } from "vue";

const emit = defineEmits(["submitPii", "errorSubmittingPii", "back"]);

const isDark = useDark();

const isLoading = ref(false);

const props = defineProps<{
  email: string;
}>();

const formData = reactive({
  firstName: "",
  lastName: "",
});

const isFormValid = computed(() => {
  return (
    formData.firstName.trim() !== "" &&
    formData.lastName.trim() !== ""
  );
});

const handleContinue = async () => {
  if (!isFormValid.value || isLoading.value) return;

  isLoading.value = true;
  try {
    const account = await userApi.createAccount(props.email, formData.firstName, formData.lastName, false);
    await authApi.requestCode(null, account.id, true);
    emit("submitPii", {
      email: props.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
    });
  } catch (error) {
    const e = error as SubmitPiiError;
    console.error("Submit error:", e);
    if (e === 'INVALID_INPUT') {
      emit("errorSubmittingPii");
    }
  } finally {
    isLoading.value = false;
  }
};

const back = () => {
  emit("back");
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.enter-pii {
  display: flex;
  flex-direction: column;
  justify-content: center;

  min-height: 100vh;
  width: 40%;
  max-width: 718px;

  @media screen and (max-width: $medium-screen) {
    width: 85%;
  }
}

.logo-wrapper {
  display: flex;
  margin-bottom: 2rem;

  @media screen and (max-width: $medium-screen) {
    justify-content: center;
    margin-bottom: 1rem;
  }

  .logo {
    height: 32px;

    @media screen and (max-width: $medium-screen) {
      height: 25px;
    }
  }
}

.enter-pii-title {
  margin-bottom: 2rem;
  font-size: 32px;
  font-weight: 600;
  color: var(--secondary-color);

  strong {
    font-weight: 700;
  }

  @media screen and (max-width: $medium-screen) {
    font-size: 20px !important;
    margin-bottom: 1rem;
    text-align: center;
    line-height: normal !important;
  }
}

.form-group {
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-bottom: 1.2rem;
}

.form-label {
  font-size: 16px;
  font-weight: 500;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;

  @media screen and (max-width: $medium-screen) {
    font-size: 14px !important;
  }
}

.form-input {
  padding: 0.7rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 16px;
  font-family: inherit;
  color: var(--secondary-color);
  background-color: var(--white-color);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: $primary-color;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
  }

  &::placeholder {
    color: #999;
  }

  @media screen and (max-width: $medium-screen) {
    font-size: 16px !important;
    padding: 0.7rem 0.5rem;
  }
}

.form-row {
  display: grid;
  width: 100%;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.2rem;

  .form-group {
    margin-bottom: 0;
  }

  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;

    .form-group {
      margin-bottom: 0.75rem;
    }
  }
}

.btn-group {
  display: flex;
  gap: 8px;
  margin-bottom: 1.5rem;

   @media screen and (max-width: $medium-screen) {
    margin-bottom: 1rem;
  }

  .btn-back {
    padding: 1rem;
    font-size: 16px;
    font-weight: 600;
    background-color: var(--light-grey-color);
    border: none;
    border-radius: 4px;

    &:hover:not(:disabled) {
      background-color: darken($light-grey-color, 8%);
    }

    @media screen and (max-width: $medium-screen) {
      font-size: 11px;
      padding: 0.7rem;
    }
  }

  .btn-continue {
    width: 100%;
    padding: 1rem;
    background-color: $primary-color;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 600;
    color: var(--secondary-color);
    cursor: pointer;
    transition: background-color 0.2s ease;
  
    @media screen and (max-width: $medium-screen) {
      font-size: 11px !important;
      padding: 0.7rem;
    }
  
    &:hover:not(:disabled) {
      background-color: darken($primary-color, 8%);
    }
  
    &:active:not(:disabled) {
      background-color: darken($primary-color, 16%);
    }
  
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  
    i {
      animation: spin 1s linear infinite;
    }
  }
}


@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.terms-text {
  font-size: 13px;
  color: #999;
  line-height: 1.4;
  
  a {
    color: var(--secondary-color);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media screen and (max-width: $medium-screen) {
    text-align: center;
    font-size: 9px !important;
    margin-bottom: 2rem;
  }
}

@media screen and (max-width: $large-screen) {
  .enter-pii-title {
    font-size: 32px;
  }
}
</style>
